/**
 * DM WebSocket client (web). Wraps the browser's native WebSocket
 * with auto-reconnect (exponential backoff capped at 30s) + a typed
 * event subscription API.
 *
 * Wire format (matches backend `internal/dm/ws_protocol.go`):
 *   server → client: hello, message.new, message.read, typing.start,
 *                    typing.stop, thread.blocked, pong
 *   client → server: typing.start, typing.stop, ping
 *
 * Auth: ?access_token=<jwt> query param. The native WebSocket API
 * can't carry custom headers, and Sec-WebSocket-Protocol is fiddly
 * — query-param-over-TLS is the standard browser pattern.
 *
 * Usage:
 *   const ws = openDMSocket({
 *     onMessage: (e) => ...,
 *     onTyping:  (e) => ...,
 *     onRead:    (e) => ...,
 *     onBlocked: (e) => ...,
 *     onOpen:    () => ...,
 *     onClose:   () => ...,
 *   });
 *   // later:
 *   ws.sendTyping(threadId, true);
 *   ws.close();
 *
 * Reconnect cadence: 1s, 2s, 4s, 8s, 16s, capped 30s. Resets on
 * successful connect.
 */
import { sessionStore } from './api';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:8080';

// Translate http(s)://… → ws(s)://… for the upgrade path.
function wsBase(): string {
  return API_BASE.replace(/^http(s?):\/\//, 'ws$1://');
}

export type DMMessageEvent = {
  type: 'message.new';
  thread_id: string;
  message: {
    id: string;
    thread_id: string;
    sender_id: string;
    body: string;
    read_at?: string | null;
    created_at: string;
  };
};

export type DMTypingEvent = {
  type: 'typing.start' | 'typing.stop';
  thread_id: string;
  by_user_id: string;
};

export type DMReadEvent = {
  type: 'message.read';
  thread_id: string;
  up_to_message_id: string;
  by_user_id: string;
  at: string;
};

export type DMBlockedEvent = {
  type: 'thread.blocked';
  thread_id: string;
  by_user_id: string;
};

export type DMHelloEvent = {
  type: 'hello';
  user_id: string;
};

type Handlers = {
  onHello?:    (e: DMHelloEvent) => void;
  onMessage?:  (e: DMMessageEvent) => void;
  onTyping?:   (e: DMTypingEvent) => void;
  onRead?:     (e: DMReadEvent) => void;
  onBlocked?:  (e: DMBlockedEvent) => void;
  onOpen?:     () => void;
  onClose?:    (code: number, reason: string) => void;
  onError?:    (err: Event) => void;
};

export interface DMSocket {
  /** Underlying readyState (0=CONNECTING 1=OPEN 2=CLOSING 3=CLOSED). */
  readyState(): number;
  sendTyping(threadId: string, typing: boolean): void;
  /** Stops auto-reconnect + closes the socket. */
  close(): void;
}

export function openDMSocket(handlers: Handlers): DMSocket {
  let ws: WebSocket | null = null;
  let closed = false;
  let attempt = 0;
  let pingTimer: ReturnType<typeof setInterval> | null = null;

  function backoff() {
    return Math.min(30_000, 1000 * Math.pow(2, attempt));
  }

  function connect() {
    if (closed) return;
    const access = sessionStore.getAccess();
    if (!access) {
      // Don't reconnect if signed out; the caller's auth flow will
      // re-open after they sign in again.
      closed = true;
      handlers.onClose?.(4001, 'no access token');
      return;
    }
    const url = wsBase() + '/dm/ws?access_token=' + encodeURIComponent(access);
    ws = new WebSocket(url);

    ws.onopen = () => {
      attempt = 0;
      handlers.onOpen?.();
      // Client-side keepalive ping every 30s.
      if (pingTimer) clearInterval(pingTimer);
      pingTimer = setInterval(() => {
        if (ws?.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30_000);
    };

    ws.onmessage = (e) => {
      try {
        const frame = JSON.parse(e.data as string) as { type: string };
        switch (frame.type) {
          case 'hello':         handlers.onHello?.(frame as DMHelloEvent);     break;
          case 'message.new':   handlers.onMessage?.(frame as DMMessageEvent); break;
          case 'message.read':  handlers.onRead?.(frame as DMReadEvent);       break;
          case 'typing.start':
          case 'typing.stop':   handlers.onTyping?.(frame as DMTypingEvent);   break;
          case 'thread.blocked': handlers.onBlocked?.(frame as DMBlockedEvent); break;
          case 'pong':
          case 'ping':          /* heartbeat; ignore */                        break;
        }
      } catch {
        // Malformed frame — log + drop.
        console.warn('DM ws: invalid frame', e.data);
      }
    };

    ws.onerror = (e) => handlers.onError?.(e);

    ws.onclose = (e) => {
      if (pingTimer) {
        clearInterval(pingTimer);
        pingTimer = null;
      }
      handlers.onClose?.(e.code, e.reason);
      if (closed) return;
      // Reconnect with backoff.
      attempt++;
      setTimeout(connect, backoff());
    };
  }

  connect();

  return {
    readyState: () => ws?.readyState ?? WebSocket.CLOSED,
    sendTyping(threadId, typing) {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type: typing ? 'typing.start' : 'typing.stop',
          thread_id: threadId,
        }));
      }
    },
    close() {
      closed = true;
      if (pingTimer) clearInterval(pingTimer);
      ws?.close(1000, 'client closing');
    },
  };
}
